import Component from '@glimmer/component'
import { service } from '@ember/service'
import { assert } from '@ember/debug'
import { isWithinInterval, subDays, format } from 'date-fns'
import type { IntlService } from 'ember-intl'
import type SchoolSubscription from 'teacher-ui-client/models/school-subscription'
import { t } from 'ember-intl'
import { or, not } from 'ember-truth-helpers'
import { InlineAlert, AlertType, InlineAlertSubtitle } from 'teacher-ui-client/components/inline-alert'

interface Signature {
  Element: HTMLDivElement
  Args: {
    schoolSubscription?: SchoolSubscription
    schoolName?: string
    showCautionDaysBeforeExpiry?: number
    trialTeacherCount?: number
    trialStudentCount?: number
  }
}

export class SubscriptionFormSummary extends Component<Signature> {
  @service intl!: IntlService

  get subscriptionEndDate() {
    const date = this.args.schoolSubscription?.endDate
    if (date) return format(date, this.intl.t('dateFormats.dateFns.MMMM dd, yyyy'))
    return '-'
  }

  get maxStudentCapacity() {
    if (this.args.schoolSubscription?.subscriptionUtilisation?.isUnlimitedCapacity) {
      return this.intl.t('unlimited')
    }
    return this.args.schoolSubscription?.subscriptionUtilisation?.licenceCount
  }

  get schoolSubscriptionIsUtilised() {
    return Boolean(this.args.schoolSubscription?.subscriptionUtilisation)
  }

  get activeStudentCount() {
    return this.args.schoolSubscription?.subscriptionUtilisation?.studentCount ?? 0
  }

  get trialTeacherCount() {
    return this.args.trialTeacherCount ?? 0
  }

  get trialStudentCount() {
    return this.args.trialStudentCount ?? 0
  }

  get alertType() {
    if (!this.args.schoolSubscription) return AlertType.Warning
    if (this.hasExpired) return AlertType.Critical
    if (this.willExpireSoon || this.isOverCapacity) return AlertType.Warning
    return AlertType.Success
  }

  get hasExpired() {
    return Boolean(this.args.schoolSubscription?.isExpired)
  }

  get willExpireSoon() {
    const showCautionDaysBeforeExpiry = 60
    const endDate = this.args.schoolSubscription?.endDate
    if (endDate instanceof Date) {
      return isWithinInterval(new Date(), {
        start: subDays(endDate, showCautionDaysBeforeExpiry),
        end: endDate,
      })
    }
    return false
  }

  /**
   * Your {Reading Eggs/Mathseeds/Writing Legends} subscription expires on {date}
   */
  get subscriptionExpiryText() {
    assert('missing school subscription', this.args.schoolSubscription)
    const { subscriptionType, isExpired } = this.args.schoolSubscription

    const subscriptionName = this.intl.t(`subscriptions.${subscriptionType}`)
    return this.intl.t('components.subscriptionSummary.schoolSubExpiry', { subscriptionName, isExpired })
  }

  get isOverCapacity() {
    return this.args.schoolSubscription?.subscriptionUtilisation?.isOverCapacity
  }

  <template>
    <InlineAlert @type={{this.alertType}} ...attributes>
      <InlineAlertSubtitle>
        <ul>
          <li>
            {{#if (or (not @schoolSubscription) this.hasExpired)}}
              <p class="mb-3">{{t "components.subscriptionSummary.noActiveSubscription"}}</p>
            {{/if}}
            {{#if @schoolSubscription}}
              <span>{{this.subscriptionExpiryText}}</span>
              <strong>{{this.subscriptionEndDate}}</strong>
            {{/if}}
          </li>
          {{#if this.schoolSubscriptionIsUtilised}}
            <li>
              {{t "components.subscriptionSummary.activeStudentsLabel"}}
              <strong>
                {{this.activeStudentCount}}
                /
                {{this.maxStudentCapacity}}
                {{#if this.isOverCapacity}}
                  &mdash;
                  {{t "components.subscriptionSummary.limitExceededLabel"}}
                {{/if}}
              </strong>
            </li>
          {{/if}}
          <li>
            {{t
              "components.subscriptionSummary.activeTrials"
              teachers=(t "resourceCounts.teachers" count=this.trialTeacherCount)
              students=(t "resourceCounts.students" count=this.trialStudentCount)
            }}
          </li>
        </ul>
      </InlineAlertSubtitle>
    </InlineAlert>
  </template>
}
